
import {defineComponent, PropType} from 'vue'
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import ProductSelector from "@/components/UI/product/ProductSelector.vue";
import {trimLeadingZeroes} from "@/helpers/functions/number";
import InputProductList from "@/components/UI/InputProductList.vue";
import {ProductListSelectionDto} from "@/components/UI/product/ProductListSelectionDto";
import ProductTileView from "@/components/UI/product/ProductTileView.vue";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import {generateUUIDv4} from "@/helpers/functions/string";
import {FormMixin} from "@/components/base/FormMixin";
import {
  CatalogProductWithImageOverviewRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CatalogProductWithImageOverviewRestDtoModel";

export default defineComponent({
  name: "ProductList",
  mixins: [FormMixin],
  components: {InputCheckboxBoolean, ProductTileView, InputProductList, ProductSelector, AlertError2, BaseSpinner},
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Object as PropType<ProductListSelectionDto>,
      required: true,
      default: [] as string[],
    },
    errors: {
      type: Object,
      required: true,
    },
    fieldLabel: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: '',
    },
    height: {
      type: Number,
      default: 300,
    },
  },
  data() {
    return {
      cnkCodes: [] as string[],
      productCodes: [] as string[],
      gridView: true,
      showProductCodeProducts: false
    }
  },
  watch: {
    modelValue() {
      this.reloadContent();
    }
  },
  mounted() {
    this.reloadContent();
  },
  methods: {
    generateUUIDv4,
    async doReload(): Promise<void> {
      if (this.modelValue?.cnkCodes?.length) {
        this.cnkCodes = [...this.modelValue.cnkCodes].map((cnkCode: string) => trimLeadingZeroes(cnkCode));
      } else {
        this.cnkCodes = [];
      }
      if (this.modelValue?.productCodes?.length) {
        this.productCodes = [...this.modelValue.productCodes];
      } else {
        this.productCodes = [];
      }
      if (this.productCodes.length) {
        this.showProductCodeProducts = true;
      }
    },
    async addProducts(selectedProducts: CatalogProductWithImageOverviewRestDtoModel[]): Promise<void> {
      if (!selectedProducts?.length) {
        return;
      }
      const newCnkCodes: string[] = [...this.cnkCodes];
      const newProductCodes: string[] = [...this.productCodes]
      for (const product of selectedProducts) {
        if (product.product.product_info.cnk_code) {
          const trimmedCnk: string = trimLeadingZeroes(product.product.product_info.cnk_code);
          // It is possible that the product code of a product with a CNK code was entered manually in the
          // TinyMCE editor.
          if (!newCnkCodes.includes(trimmedCnk) && !newProductCodes.includes(product.product.product_code)) {
            newCnkCodes.push(trimmedCnk);
          }
        } else {
          if (!newProductCodes.includes(product.product.product_code)) {
            newProductCodes.push(product.product.product_code);
          }
        }
      }

      this.cnkCodes = [...new Set(newCnkCodes)];
      this.productCodes = [...new Set(newProductCodes)];
      this.emitUpdatedModelValueAndUpdateUI();
    },
    async switchToGridView(): Promise<void> {
      this.gridView = true;
    },
    switchToListView(): void {
      this.gridView = false;
    },
    cnksUpdated(newCnkCodes: string[]): void {
      const trimmedCnkCodes: string[] = [...newCnkCodes].map((cnkCode: string) => trimLeadingZeroes(cnkCode));

      this.cnkCodes = [...new Set(trimmedCnkCodes)];
      this.emitUpdatedModelValueAndUpdateUI();
    },
    productCodesUpdated(newProductCodes: string[]): void {
      this.productCodes = [...new Set(newProductCodes)];
      this.emitUpdatedModelValueAndUpdateUI();
    },
    emitUpdatedModelValueAndUpdateUI(): void {
      if (this.productCodes.length) {
        this.showProductCodeProducts = true;
      }
      this.$emit('update:modelValue', {
        cnkCodes: this.cnkCodes,
        productCodes: this.productCodes
      });
    }
  }
});
